/**
 * Utility functions for formatting numbers and credits in Indian format
 * This file provides consistent formatting across the application
 */

/**
 * Format credits in Indian format with comma separators
 * Example: 1234567 -> 12,34,567
 * @param {number} credits - The number of credits to format
 * @returns {string} Formatted credits string
 */
export const formatCredits = (credits) => {
  // Handle null, undefined, or non-numeric values
  if (credits === null || credits === undefined || isNaN(credits)) {
    return '0';
  }
  
  // Convert to number and handle edge cases
  const numCredits = Number(credits);
  if (numCredits === 0) return '0';
  
  // Format in Indian numbering system (lakhs, crores)
  return numCredits.toLocaleString('en-IN');
};

/**
 * Format currency in Indian format with comma separators
 * Example: 1234567 -> ₹12,34,567.00
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: 'INR')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '₹0.00';
  }
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Format large numbers in Indian format with appropriate suffixes
 * Example: 1234567 -> 12.35 Lakhs
 * @param {number} number - The number to format
 * @returns {string} Formatted number string
 */
export const formatLargeNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0';
  }
  
  const num = Number(number);
  if (num === 0) return '0';
  
  if (num >= 10000000) {
    return (num / 10000000).toFixed(2) + ' Crores';
  } else if (num >= 100000) {
    return (num / 100000).toFixed(2) + ' Lakhs';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  
  return num.toLocaleString('en-IN');
}; 